// Lute for the medieval style (docs/AUDIO_DESIGN.md §12). Every pluck is a double course (two gut strings a few cents
// apart, the bass courses strung in octaves) rendered as Karplus–Strong strings in JS into an AudioBuffer (cached per page)
// and played by one AudioBufferSource + one gain per note through a small bowl-back body. An optional hurdy-gurdy drone
// (tonic, or tonic + fifth) sits under it on the tracks whose harmony allows one.
import { rng, clamp } from '../../engine/core.js';
import { mtof } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

/* ---------------- plucks: two Karplus–Strong strings per course, at half the context rate ---------------- */
// One string: y[n] = e[n] for the first lap (n < L), then T(H(y[n − L]))   e = the pluck, H = the loss one-pole (meets the
// T60 at f0 and at 1.8 kHz), T = the tuning allpass (no dispersion: gut is not stiff). The course sums string A at −c/2 ¢
// and string B at +c/2 ¢ (c = 5–10 ¢ per note, fixed per pitch: the slow beating of a real course), B plucked 0.4–1.4 ms
// later at 0.65; below G3 (MIDI 55) B is the octave string (2f, 0.45), as on a Renaissance lute's bass courses.
const CACHE = new Map();                              // `${rate}:${midi}` → AudioBuffer, built once, lazily
export const LUTE_STATS = { built: 0, ms: 0, maxMs: 0 };
const FB = 1800, TWO_PI = Math.PI * 2;
//   pick position β · flesh LP (Hz) · noise share · T60 at 1.8 kHz (s): plucked near the bridge, so brighter than a guitar
const TOP = { beta: 0.11, lp: 2500, noise: 0.25, tb: 0.3 }, BASS = { beta: 0.15, lp: 1500, noise: 0.18, tb: 0.24 };
const apDelay = (c, w) => 1 - 2 * Math.atan2(c * Math.sin(w), 1 + c * Math.cos(w)) / w;   // 1st-order allpass phase delay
/** T60 of the fundamental: 2.3·(196/f)^0.5 s (G2 3.3 s · G3 2.3 s · G4 1.6 s): a lute dies away faster than a guitar */
const t60 = f => clamp(2.3 * Math.pow(196 / f, 0.5), 0.8, 3.4);

/** one string of length N (the delay line is the output), added into y at `off` samples, × amp */
function string(rate, f, R, seed, y, off, amp) {
  const P = rate / f, w0 = TWO_PI * f / rate, wb = TWO_PI * FB / rate, T0 = t60(f), N = y.length - off;
  const A0 = Math.pow(10, -3 / (f * T0)), want = Math.pow(10, -3 / (f * Math.min(R.tb, 0.8 * T0))) / A0;
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 36; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, b0 = (A0 / mag(a, w0)) * (1 - a);
  const rest = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0, L = Math.max(2, Math.floor(rest - 0.5)), d = rest - L;
  let cl = -0.9, ch = 0.9;
  for (let k = 0; k < 36; k++) { const m = (cl + ch) / 2; if (apDelay(m, w0) > d) cl = m; else ch = m; }
  const C = (cl + ch) / 2;
  // the pluck: a triangle peaking at β·L + noise combed at the same point, rounded by the flesh (a periodic one-pole LP
  // run twice), mean removed; the loop filters are primed with that lap so the second lap follows without a step
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), nz = new Float64Array(L), r = rng(seed >>> 0);
  const al = 1 - Math.exp(-TWO_PI * R.lp / rate);
  for (let i = 0; i < L; i++) nz[i] = r() * 2 - 1;
  let ns = 0;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  let z = 0, z2 = 0, mean = 0;
  for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (e[i] - z); z2 += al * (z - z2); if (lap) e[i] = z2; }
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) e[i] -= mean;
  let h = 0, tx = 0, ty = 0;
  const loop = x => { h = b0 * x + a * h; const tv = C * h + tx - C * ty; tx = h; ty = tv; return tv; };
  for (let i = 0; i < L; i++) loop(e[i]);
  const s = new Float32Array(N);
  for (let n = 0; n < N; n++) s[n] = n < L ? e[n] : loop(s[n - L]);
  for (let n = 0; n < N; n++) y[n + off] += amp * s[n];
}

function render(rate, midi) {
  const f0 = mtof(midi), u = rng((midi * 7919 + 31) >>> 0), c = 5 + 5 * u(), octave = midi < 55, R = octave ? BASS : TOP;
  // the buffer lasts 0.55 of the lower string's T60 (1–1.8 s), then fades
  const N = Math.round(clamp(0.55 * t60(f0), 1, 1.8) * rate), y = new Float32Array(N), lag = Math.round((0.0004 + 0.001 * u()) * rate);
  string(rate, f0 * Math.pow(2, -c / 2400), R, midi * 131 + 7, y, 0, 1);
  if (octave) string(rate, 2 * f0 * Math.pow(2, c / 4800), TOP, midi * 131 + 11, y, lag, 0.45);
  else string(rate, f0 * Math.pow(2, c / 2400), R, midi * 131 + 11, y, lag, 0.65);
  // the top hears the force at the bridge, the strings' slope y[n] − y[n−1] (+6 dB/oct), then a DC blocker (~20 Hz), a 2 ms
  // attack ramp, a 0.2 s end fade, and RMS of 0–0.3 s → 0.2
  const hp = 1 - TWO_PI * 20 / rate, na = Math.max(2, Math.round(0.002 * rate)), nf = Math.round(0.2 * rate), n3 = Math.min(N, Math.round(0.3 * rate));
  let px = 0, py = 0, yl = 0, ss = 0;
  for (let n = 0; n < N; n++) {
    const sl = y[n] - yl; yl = y[n];
    py = sl - px + hp * py; px = sl;
    let v = py;
    if (n < na) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    y[n] = v;
    if (n < n3) ss += v * v;
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) y[n] *= k;
  return y;
}

/** The cached course of one note, rendered on first use. */
export function course(ac, midi) {
  const rate = ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate, key = `${rate}:${midi}`;
  let b = CACHE.get(key);
  if (!b) {
    const t = performance.now(), y = render(rate, midi);
    b = ac.createBuffer(1, y.length, rate);
    b.getChannelData(0).set(y);
    CACHE.set(key, b);
    const ms = performance.now() - t;
    LUTE_STATS.built++; LUTE_STATS.ms += ms; LUTE_STATS.maxMs = Math.max(LUTE_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the instrument ---------------- */
// Levels (tuning knobs): the lute against the EP it replaces, its bass course, the reverb send, the drone.
export const LUTE = { level: 9.8, bass: 1.05, send: 0.3, drone: 0.04 };
const COURSES = 7;                                   // G2 C3 F3 A3 D4 G4 + a chanterelle: a 4th-ish apart from MIDI 43
const courseOf = m => clamp(Math.floor((m - 43) / 5), 0, COURSES - 1);

/**
 * createLute(ac, out, send, plan, opts) → { chord, note, warm, stats, dispose }   (the style's `ep`)
 * plan.ep.lp: the bus low-pass; plan.medieval.drone: null | [0] | [0, 7] (semitones over the tonic).
 * opts: { registry, tag, cap, r, t0, log, stepDur }.
 * Bus: 7 course panners (−.22 … +.22) → HP 80 Hz → bowl 125 Hz (+1.5 dB, Q 1.4) → nasal 1.15 kHz (+2.5 dB, Q 1.2) → presence
 * 3.2 kHz (+1.5 dB) → allpasses 700 / 1900 Hz (Q .7: the body smears the bridge pulse, less crest) → LP plan.ep.lp (Q .6)
 * → out ×9.8, reverb send .3. Per note: A = .16·v^1.3 (bass courses ×1.05), ±2 ¢, release τ = the event's rel. Each note
 * sounds on a course (a 4th-wide band of pitches; a chord's notes take distinct courses from the top down): a new pluck on
 * a ringing course damps it (τ 12 ms), so the lute never has more than 7 courses (plus their 12 ms tails) sounding.
 * Drone: 2 saws (the tonic at MIDI 36–47, the fifth .6) → LP 480 Hz → the body, ±25 % breathing at 0.09 Hz, level .04: in
 * over the intro, +2.5 dB in the break, out over the outro's final chord.
 */
export function createLute(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, stepDur = 0.2 }) {
  const nodes = [], src = [], N = x => (nodes.push(x), x);
  const busIn = N(gain(ac, 1)), pans = Array.from({ length: COURSES }, (_, k) => N(panner(ac, -0.22 + (0.44 * k) / (COURSES - 1))));
  const hp = N(filter(ac, 'highpass', 80, 0.7)), bowl = N(filter(ac, 'peaking', 125, 1.4, 1.5)), nasal = N(filter(ac, 'peaking', 1150, 1.2, 2.5));
  const pres = N(filter(ac, 'peaking', 3200, 1, 1.5)), aps = [700, 1900].map(f => N(filter(ac, 'allpass', f, 0.7))), lp = N(filter(ac, 'lowpass', plan.ep.lp, 0.6)), outG = N(gain(ac, LUTE.level)), sendG = N(gain(ac, LUTE.send));
  for (const p of pans) p.connect(busIn);
  const chain = [busIn, hp, bowl, nasal, pres, ...aps, lp, outG];
  chain.forEach((n, k) => k && chain[k - 1].connect(n));
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  const dr = plan.medieval && plan.medieval.drone;
  if (dr) {
    const m = 36 + ((((plan.key.tonic - 36) % 12) + 12) % 12), bar = 16 * stepDur;
    const dLP = N(filter(ac, 'lowpass', 480, 0.7)), dMix = N(gain(ac, 1)), dAmp = N(gain(ac, 0)), lfo = N(osc(ac, 'sine', 0.09)), lfoG = N(gain(ac, 0.25));
    dr.forEach((iv, k) => { const o = N(osc(ac, 'sawtooth', mtof(m + iv))), g = N(gain(ac, k ? 0.6 : 1)); o.connect(g); g.connect(dLP); src.push(o); });
    dLP.connect(dMix); dMix.connect(dAmp); dAmp.connect(hp);
    lfo.connect(lfoG); lfoG.connect(dMix.gain); src.push(lfo);
    const D = LUTE.drone;
    let ts = t0;
    for (const s of plan.sections) {
      if (s.name === 'intro') dAmp.gain.setTargetAtTime(0.7 * D, ts + bar * 0.5, (s.bars * bar) / 3);
      else if (s.name === 'outro') dAmp.gain.setTargetAtTime(0, ts + 2 * bar, 0.6 * bar);
      else dAmp.gain.setTargetAtTime(s.name === 'break' ? 1.33 * D : D, ts, bar / 2);
      ts += s.bars * bar;
    }
  }
  const bus = persistent(registry, tag, src, nodes, t0);
  let voices = [], maxActive = 0, steals = 0, notes = 0, pending = [];

  const damp = (x, t) => { registry.stop(x.v, t, 0.012); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };
  /** one pluck on course c (a ringing course is damped first; over the cap the oldest note goes, 10 ms) */
  function note(t, midi, vel, dur, relTau = 0.08, c = courseOf(midi)) {
    const buf = course(ac, midi);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.c === c && x.start <= t + 1e-3) damp(x, t);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) { const old = voices.shift(); registry.stop(old.v, t, 0.01); old.end = t; if (old.rec) old.rec[1] = t; steals++; }
    const rate = Math.pow(2, (r() * 4 - 2) / 1200);                // ±2 ¢ per pluck
    const s = ac.createBufferSource(), g = gain(ac, 0);
    s.buffer = buf; s.playbackRate.value = rate;
    s.connect(g); g.connect(pans[c]);
    const A = 0.16 * Math.pow(vel, 1.3) * (midi < 55 ? LUTE.bass : 1), off = t + Math.max(0.05, dur);
    g.gain.setValueAtTime(A, t);
    g.gain.setTargetAtTime(0, off, relTau);
    const bufEnd = t + buf.duration / rate, end = Math.min(bufEnd, off + relTau * 7);
    s.start(t); s.stop(end);
    const v = registry.add({ src: [s], all: [s, g], start: t, end, tag, out: g });
    const x = { v, c, start: t, end: Math.min(bufEnd, off + relTau * 4), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  /** courses of a chord: the top note takes its own course, each lower note its own or the one under the note above */
  function courses(midis) {
    const cs = new Array(midis.length);
    let above = COURSES;
    for (let k = midis.length - 1; k >= 0; k--) { cs[k] = Math.max(0, Math.min(courseOf(midis[k]), above - 1)); above = cs[k]; }
    return cs;
  }
  /** held chords (intro, break, outro: rel ≥ .1), as a lutenist fills a long chord: a slow roll up (50–100 ms a string) on
   *  beat 1, thumb + top on beat 3 with the inner fingers on the next two 8ths (.7 / .58); the final chord (rel ≥ .5) is one
   *  slow roll (130 ms a string), then the bass and the top once more a bar later, left to ring. The strikes after the
   *  first count back from the chord's end on the grid (t + dur + 30 ms: the engine's hits end 30 ms early), so a chord
   *  struck again part-way through (the radio tuning in) plays on exactly the strikes it had. */
  function held(t, midis, vel, dur, relTau) {
    const cs = courses(midis), n = midis.length, half = 8 * stepDur, gap = clamp(0.45 * stepDur, 0.05, 0.1);
    const end = t + dur + 0.03, later = x => x > t + 0.05;
    if (relTau >= 0.5) {
      midis.forEach((m, k) => note(t + k * 0.13, m, vel * (0.9 + 0.05 * k), dur, relTau, cs[k]));
      const tb = end - 16 * stepDur;
      if (later(tb)) { note(tb, midis[0], vel * 0.6, end - 0.03 - tb, relTau, cs[0]); note(tb + 0.09, midis[n - 1], vel * 0.5, end - 0.03 - tb, relTau, cs[n - 1]); }
      return;
    }
    const roll = (t0, v0, left) => midis.forEach((m, k) => note(t0 + k * gap * (1 + (r() - 0.5) * 0.2), m, v0 * (0.88 + 0.04 * k), left - k * gap, relTau, cs[k]));
    roll(t, vel, dur);
    let halves = 0;                                  // up to 3 more, a half bar apart back from the end, after now
    while (halves < 3 && later(end - (halves + 1) * half)) halves++;
    for (let j = halves; j >= 1; j--) {             // j halves before the end, in time order (a pluck damps its string)
      const tj = end - j * half, left = end - 0.03 - tj;
      if (j % 2 === 0) { roll(tj, vel * 0.9, left); continue; }
      // beat 3: thumb + top together, the inner fingers on the next 8ths
      const seq = [[0, 0], [n - 1, 0.012], ...(n > 2 ? [[n - 2, 2 * stepDur]] : []), ...(n > 3 ? [[1, 4 * stepDur]] : [])];
      seq.forEach(([k, dt], q) => { if (dt < left) note(tj + dt, midis[k], vel * (q < 2 ? 0.7 : 0.58), left - dt, relTau, cs[k]); });
    }
  }
  return {
    note,
    /** rolled bottom to top over `strum` s (20–50 ms from the energy row); the top voice +6 %, ±5 % per note */
    chord(t, midis, vel, dur, strum = 0.03, relTau = 0.08) {
      if (relTau >= 0.095 && midis.length >= 3 && dur >= 7.5 * stepDur) held(t, midis, vel, dur, relTau);
      else {
        const cs = courses(midis), n = midis.length;
        midis.forEach((m, k) => {
          const dt = n > 1 ? (k * strum) / (n - 1) : 0, v = vel * (k === n - 1 && n > 1 ? 1.06 : 1) * (1 + (r() * 2 - 1) * 0.05);
          note(t + dt, m, Math.min(1, v), dur - dt, relTau, cs[k]);
        });
      }
      if (pending.length) course(ac, pending.shift());
    },
    /** render the courses of these voicings (the track's chords): now within `ms` of JS, the rest one per chord call */
    warm(chords, ms = 3) {
      const t = performance.now(), seen = new Set();
      for (const v of chords) for (const m of v) {
        if (seen.has(m)) continue;
        seen.add(m);
        if (performance.now() - t < ms) course(ac, m); else pending.push(m);
      }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}
